import type { ReactNode } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { AppShell } from '@/components/AppShell/AppShell';
import { QuotesListView } from '../QuotesListView';
import { ApplicationsListView } from '../ApplicationsListView';
import { PoliciesListView } from '../PoliciesListView';
import { BindFormsListView } from '../BindFormsListView';
import { AnalyticsView } from '../AnalyticsView';
import AggregationMapView from '../AggregationMapView/AggregationMapView';
import PmlSnapshotPage from '../PmlSnapshot/PmlSnapshotPage';
import { ProtectedRoute as ProtectedRouteJs } from '../../routing/ProtectedRoute';
import { DashboardScrollRegions } from './DashboardScrollRegions';

type ValidateArgs = { auth: { loggedIn?: boolean; user?: { isInternal?: boolean } } };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: routing/ProtectedRoute.jsx
const ProtectedRoute = ProtectedRouteJs as any as (props: {
  exact?: boolean;
  path: string;
  validate?: (args: ValidateArgs) => boolean;
  children: ReactNode;
}) => JSX.Element;
// The list views and maps are JavaScript components; these are the props the dashboard passes.
const List = (Component: unknown) => Component as (props: { searchPath?: string }) => JSX.Element;

const Quotes = List(QuotesListView);
const Applications = List(ApplicationsListView);
const Policies = List(PoliciesListView);
const BindForms = List(BindFormsListView);
const Analytics = List(AnalyticsView);
const AggregationMap = List(AggregationMapView);
const PmlSnapshot = List(PmlSnapshotPage);

/**
 * The signed-in app: the shell (sidebar and header strip) around the dashboard's two legacy scroll
 * regions. The list region holds the list views and maps by path; the detail region holds whatever
 * DashboardRoutes renders (`children`).
 */
export const Dashboard = ({ children }: { children?: ReactNode }) => {
  // The Policies, Quotes and Applications tables scroll themselves; there the list region holds
  // still (Shell layout, D35).
  const onTableList = useRouteMatch({ path: ['/policies', '/quotes', '/applications'], exact: true });
  // Admin › Portal Users is the same table, in the detail region.
  const onTableDetail = useRouteMatch({ path: '/admin/portal-users/:userId?', exact: true });
  return (
    <AppShell>
      <DashboardScrollRegions
        listRegionScrolls={!onTableList}
        detailRegionScrolls={!onTableDetail}
        list={
          <Switch>
            <Route exact path="/applications">
              <Applications searchPath="/applications" />
            </Route>
            <Route exact path="/quotes">
              <Quotes searchPath="/quotes" />
            </Route>
            <Route path="/bind-forms">
              <BindForms searchPath="/bind-forms" />
            </Route>
            <Route exact path="/policies">
              <Policies searchPath="/policies" />
            </Route>
            <Route path="/analytics">
              <Analytics />
            </Route>
            <ProtectedRoute exact path="/aggregation/pml-snapshot" validate={({ auth }) => !!(auth.loggedIn && auth.user && auth.user.isInternal)}>
              <PmlSnapshot />
            </ProtectedRoute>
            <Route path="/aggregation">
              <AggregationMap />
            </Route>
            <Route exact path="/">
              <Redirect to="/quotes" />
            </Route>
          </Switch>
        }
      >
        {children}
      </DashboardScrollRegions>
    </AppShell>
  );
};
